'use client';

/**
 * Toast 通知组件
 * 显示全局通知消息，支持多种类型和自动消失
 */
import { useCallback, useEffect, useState } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useToastStore, ToastType, ToastAction } from '@/lib/stores/toast-store';
import { useTranslations } from 'next-intl';

// 图标配置
const iconMap: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5" />,
  error: <AlertCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  info: <Info className="w-5 h-5" />,
};

// 样式配置
const styleMap: Record<ToastType, string> = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  warning: 'bg-amber-50 border-amber-200 text-amber-900',
  info: 'bg-[#F1FAFD] border-[#4ABDE9]/30 text-[#203644]',
};

const iconColorMap: Record<ToastType, string> = {
  success: 'text-green-500',
  error: 'text-red-500',
  warning: 'text-amber-600',
  info: 'text-[#1787B2]',
};

interface ToastItemProps {
  type: ToastType;
  message: string;
  title?: string;
  details?: string;
  duration?: number;
  action?: ToastAction;
  onClose: () => void;
}

function ToastItem({ type, title, message, details, duration = 4000, action, onClose }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const t = useTranslations('toast');

  const handleClose = useCallback(() => {
    if (isExiting) return; // 防止重复触发
    setIsExiting(true);
    setTimeout(onClose, 200); // 等待退出动画完成
  }, [isExiting, onClose]);

  useEffect(() => {
    // 进入动画
    requestAnimationFrame(() => {
      setIsVisible(true);
    });

    // 自动关闭逻辑
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`
        flex w-full items-start gap-3 rounded-2xl border px-4 py-3 shadow-[var(--studio-shadow-overlay)]
        transform transition-all duration-200 ease-out motion-reduce:transition-none
        ${styleMap[type]}
        ${isVisible && !isExiting ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
      `}
    >
      <span className={`mt-0.5 shrink-0 ${iconColorMap[type]}`}>
        {iconMap[type]}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0 flex-1">
          {title ? (
            <p className="break-words text-sm font-semibold leading-5">{title}</p>
          ) : null}
          <p className={title ? 'break-words text-sm leading-5 opacity-90' : 'break-words text-sm font-medium leading-5'}>
            {message}
          </p>
          {details ? (
            <p className="mt-1 break-all font-mono text-[11px] leading-4 opacity-70">{details}</p>
          ) : null}
        </div>
        {action ? (
          <button
            onClick={() => {
              action.onClick();
              handleClose();
            }}
            className="inline-flex min-h-8 shrink-0 items-center self-start rounded-md px-2 py-1 text-sm font-semibold underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1 sm:self-center"
          >
            {action.label}
          </button>
        ) : null}
      </div>
      <button
        onClick={handleClose}
        aria-label={t('closeNotification')}
        data-studio-control
        className="grid min-h-8 min-w-8 shrink-0 place-items-center rounded-full transition-colors hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-1 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * Toast 容器组件
 * 放置在 app 根组件中，用于显示所有 toast 消息
 */
export function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed left-1/2 top-3 z-[9999] flex w-[calc(100vw-24px)] max-w-sm -translate-x-1/2 flex-col items-stretch gap-2 sm:top-4">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          details={toast.details}
          duration={toast.duration}
          action={toast.action}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

export default ToastContainer;
